import { useMemo } from 'react'
import clientEnv from '../../utils/global/clientEnv'

interface OneTrustConfig {
  oneTrust?: {
    allowCookieOptOut: boolean
    includeAutoBlockScript: boolean
    domainUuid: string
    reverse_banner_button_position?: string
    assetDomain?: string
  }
}

/**
 * Hook to get OneTrust configuration from store settings
 *
 * This hook retrieves the privacy compliance provider configuration
 * from the current store configuration and extracts OneTrust settings.
 *
 * For local development, it falls back to environment variables if
 * store configuration is not available.
 *
 * @returns {OneTrustConfig | null} OneTrust configuration or null if not available
 */
export const useOneTrustConfig = (): OneTrustConfig | null => {
  const oneTrustConfig = useMemo(() => {
    // TODO: Implement store configuration retrieval
    // This requires access to store configuration context which may not be
    // available at the AppProvider level. Consider:
    // 1. Moving this component deeper in the tree where store config is available
    // 2. Using a different approach to access store configuration
    // 3. Fetching configuration directly via API

    // For now, fall back to environment variables (local development)
    const domainUuid = clientEnv.PUBLIC_CLIENT_ONETRUST_DOMAIN_UUID

    if (!domainUuid) {
      return null
    }

    // Return default configuration using environment variable
    return {
      oneTrust: {
        domainUuid: domainUuid,
        allowCookieOptOut: true,
        includeAutoBlockScript: false,
        assetDomain: 'cdn.cookielaw.org',
      },
    }
  }, [])

  return oneTrustConfig
}

/**
 * Hook to check if OneTrust is configured and enabled
 *
 * @returns {boolean} Whether OneTrust is properly configured
 */
export const useIsOneTrustConfigured = (): boolean => {
  const config = useOneTrustConfig()
  return Boolean(config?.oneTrust?.domainUuid)
}

export default useOneTrustConfig
